import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem,
  Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import DraggableDialog from '../common/DraggableDialog';
import apiService from '../../services/api.service';
import { showSnackbar } from '../../store/slices/uiSlice';
import { useIsNarrow } from '../../hooks/useBreakpoints';

/**
 * Settings › Accounts notifications › Add / edit a rule. A rule e-mails one or more accountant
 * addresses when a PO of the company is finally approved with one of the ticked payment terms,
 * and its projects / divisions (SAP Dimension 1) / departments (Dimension 2) match: All, or one
 * of the chosen codes on the PO's header or lines (backend services/accountsNotify.util).
 */

export type KindRule = { all: boolean; codes: string[] };
export type EditableRule = {
  ruleid?: number;
  name: string;
  isactive: boolean;
  paymentterms: number[];
  projects: KindRule;
  divisions: KindRule;
  departments: KindRule;
  recipients: string[];
};
export type NotifyLists = {
  terms: Array<{ code: number; name: string }>;
  projects: Array<{ code: string; name: string; division: string }>;
  divisions: Array<{ code: string; name: string }>;
  departments: Array<{ code: string; name: string }>;
};
export type MatchWindow = { from: string | null; to: string | null; poCount: number };

export const emptyRule = (): EditableRule => ({
  name: '',
  isactive: true,
  paymentterms: [],
  projects: { all: true, codes: [] },
  divisions: { all: true, codes: [] },
  departments: { all: true, codes: [] },
  recipients: [],
});

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

const join = (a: string[]) => (a.length <= 1 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);

/** The rule as one sentence (the same wording as the rules list). */
export function ruleSentence(r: EditableRule, lists: NotifyLists | null): string {
  const termName = (c: number) => lists?.terms.find((t) => t.code === c)?.name || String(c);
  const terms = !r.paymentterms.length
    ? 'no payment terms yet'
    : r.paymentterms.length === 1 ? `payment terms ${termName(r.paymentterms[0])}` : `one of ${r.paymentterms.length} payment terms`;
  const part = (k: KindRule, one: string, many: string, items: Array<{ code: string; name: string }> | undefined) => {
    if (k.all) return `any ${one}`;
    if (!k.codes.length) return `no ${one} yet`;
    if (k.codes.length > 1) return `${k.codes.length} ${many}`;
    const name = items?.find((i) => i.code === k.codes[0])?.name;
    return `${one} ${k.codes[0]}${name ? ` – ${name}` : ''}`;
  };
  return `When a PO is finally approved with ${terms}, for ${part(r.projects, 'project', 'projects', lists?.projects)}, `
    + `${part(r.divisions, 'division', 'divisions', lists?.divisions)} and ${part(r.departments, 'department', 'departments', lists?.departments)}, `
    + `e-mail ${r.recipients.length ? join(r.recipients) : 'nobody yet'}.`;
}

/** What still stops the rule from being saved (the server checks the same). */
export function ruleProblems(r: EditableRule): string[] {
  const out: string[] = [];
  const name = r.name.replace(/\s+/g, ' ').trim();
  if (name.length < 3 || name.length > 150) out.push('Give the rule a name (3 to 150 characters).');
  if (!r.recipients.length) out.push('Add at least one e-mail address.');
  if (!r.paymentterms.length) out.push('Tick at least one payment term.');
  if (!r.projects.all && !r.projects.codes.length) out.push('Pick at least one project, or choose All projects.');
  if (!r.divisions.all && !r.divisions.codes.length) out.push('Tick at least one division, or choose All divisions.');
  if (!r.departments.all && !r.departments.codes.length) out.push('Tick at least one department, or choose All departments.');
  return out;
}

const money = (currency: string, v: number) =>
  `${currency ? `${currency} ` : ''}${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errorText = (err: any, fallback: string) => {
  const d = err?.response?.data;
  if (Array.isArray(d?.errors) && d.errors.length) return d.errors.join(' ');
  return d?.message || err?.message || fallback;
};

function Step({ n, title, required, children }: { n: number; title: string; required?: boolean; children: ReactNode }) {
  return (
    <Box component="section" aria-label={title} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: { xs: 1.5, sm: 2 } }}>
      <Stack direction="row" spacing={1.25} alignItems="center" mb={1.25}>
        <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700, flex: '0 0 auto' }}>
          {n}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        {required ? <Chip size="small" label="Required" variant="outlined" /> : null}
      </Stack>
      {children}
    </Box>
  );
}

function KindToggle({ value, onChange, allLabel, someLabel, label }: {
  value: KindRule; onChange: (v: KindRule) => void; allLabel: string; someLabel: string; label: string;
}) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value.all ? 'all' : 'some'}
      onChange={(_e, next) => { if (next) onChange({ all: next === 'all', codes: value.codes }); }}
      aria-label={label}
      sx={{ mb: 1 }}
    >
      <ToggleButton value="all">{allLabel}</ToggleButton>
      <ToggleButton value="some">{someLabel}</ToggleButton>
    </ToggleButtonGroup>
  );
}

function CodeGrid({ items, value, onChange, emptyText }: {
  items: Array<{ code: string; name: string }>; value: KindRule; onChange: (v: KindRule) => void; emptyText: string;
}) {
  if (!items.length) return <Typography variant="body2" color="text.secondary">{emptyText}</Typography>;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', columnGap: 1 }}>
      {items.map((item) => (
        <FormControlLabel
          key={item.code}
          control={(
            <Checkbox
              size="small"
              checked={value.codes.includes(item.code)}
              onChange={(e) => onChange({
                all: false,
                codes: e.target.checked ? Array.from(new Set([...value.codes, item.code])) : value.codes.filter((c) => c !== item.code),
              })}
            />
          )}
          label={<span><strong>{item.code}</strong> {item.name}</span>}
        />
      ))}
    </Box>
  );
}

type Props = {
  open: boolean;
  companyId: number;
  companyLabel: string;
  rule: EditableRule | null;
  lists: NotifyLists | null;
  listsError?: string;
  termCounts: Record<string, number> | null;
  window: MatchWindow | null;
  onClose: () => void;
  onSaved: () => void;
};

export default function AccountsRuleEditor({ open, companyId, companyLabel, rule, lists, listsError, termCounts, window: countWindow, onClose, onSaved }: Props) {
  const dispatch = useDispatch();
  const narrow = useIsNarrow();
  const [draft, setDraft] = useState<EditableRule>(emptyRule());
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState('');
  const [termQuery, setTermQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [match, setMatch] = useState<any | null>(null);
  const [matching, setMatching] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);
  const matchSeq = useRef(0);

  const notify = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => dispatch(showSnackbar({ message, severity }));

  useEffect(() => {
    if (!open) return;
    setDraft(rule ? { ...emptyRule(), ...rule } : emptyRule());
    setEmailDraft(''); setEmailError(''); setTermQuery(''); setMatch(null); setPreview(null);
  }, [open, rule]);

  // What the rule would have caught, refreshed as the conditions change.
  const conditionsKey = JSON.stringify([draft.paymentterms, draft.projects, draft.divisions, draft.departments]);
  useEffect(() => {
    if (!open || !companyId) return undefined;
    const seq = ++matchSeq.current;
    setMatching(true);
    const t = setTimeout(() => {
      apiService.matchAccountsNotifyRule(companyId, draft)
        .then((res: any) => { if (seq === matchSeq.current) setMatch(res?.data || null); })
        .catch(() => { if (seq === matchSeq.current) setMatch(null); })
        .finally(() => { if (seq === matchSeq.current) setMatching(false); });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, conditionsKey]);

  const set = (patch: Partial<EditableRule>) => setDraft((d) => ({ ...d, ...patch }));
  const problems = ruleProblems(draft);
  const terms = useMemo(() => lists?.terms || [], [lists]);
  const shownTerms = useMemo(() => {
    const q = termQuery.trim().toLowerCase();
    return q ? terms.filter((t) => t.name.toLowerCase().includes(q) || String(t.code).includes(q)) : terms;
  }, [terms, termQuery]);

  const addEmail = () => {
    const parts = emailDraft.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const bad = parts.filter((p) => !EMAIL.test(p));
    if (bad.length) { setEmailError(`Enter e-mail addresses like name@company.com (${bad.join(', ')}).`); return; }
    const next = Array.from(new Set([...draft.recipients, ...parts]));
    if (next.length > 20) { setEmailError('At most 20 addresses per rule.'); return; }
    set({ recipients: next });
    setEmailDraft(''); setEmailError('');
  };

  const save = async () => {
    if (problems.length) return;
    setSaving(true);
    try {
      const body = { ...draft, name: draft.name.replace(/\s+/g, ' ').trim() };
      if (draft.ruleid) await apiService.updateAccountsNotifyRule(draft.ruleid, body);
      else await apiService.createAccountsNotifyRule(companyId, body);
      notify('Rule saved');
      onSaved();
    } catch (err: any) {
      notify(errorText(err, 'The rule could not be saved'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const res: any = await apiService.previewAccountsNotifyEmail(companyId, draft);
      setPreview({ subject: String(res?.data?.subject || ''), html: String(res?.data?.html || '') });
    } catch (err: any) {
      notify(errorText(err, 'The preview could not be prepared'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res: any = await apiService.sendAccountsNotifyTestEmail(companyId, draft);
      notify(`Test e-mail sent to ${res?.data?.to}`);
    } catch (err: any) {
      notify(errorText(err, 'The test e-mail could not be sent'), 'error');
    } finally {
      setTesting(false);
    }
  };

  const projectItems = lists?.projects || [];
  const divisionOptions = (lists?.divisions || []).map((d) => ({ ...d, count: projectItems.filter((p) => p.division === d.code).length }));
  const windowText = match?.from && match?.to ? `${match.from} – ${match.to}` : countWindow?.from && countWindow?.to ? `${countWindow.from} – ${countWindow.to}` : '';

  const actions = (
    <>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" onClick={save} disabled={saving || problems.length > 0}>{saving ? 'Saving…' : 'Save rule'}</Button>
    </>
  );

  return (
    <>
      <DraggableDialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={narrow}
        title={draft.ruleid ? `Edit rule: ${rule?.name || ''}` : 'Add a rule'}
        actions={actions}
      >
        <Typography variant="body2" color="text.secondary" mb={2}>
          {companyLabel}. Lists come from this company: payment terms from SAP B1, projects and divisions / departments from the synced master data.
        </Typography>
        {listsError ? <Alert severity="error" sx={{ mb: 2 }}>{listsError}</Alert> : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
          <Stack spacing={2} sx={{ flex: '999 1 560px', minWidth: 0 }}>
            <Step n={1} title="Name and status">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  label="Rule name"
                  value={draft.name}
                  onChange={(e) => set({ name: e.target.value })}
                  inputProps={{ maxLength: 150 }}
                  placeholder="e.g. Advance payments – all projects"
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  control={<Switch checked={draft.isactive} onChange={(e) => set({ isactive: e.target.checked })} />}
                  label={draft.isactive ? 'Active' : 'Paused'}
                />
              </Stack>
            </Step>

            <Step n={2} title="Who gets the e-mail">
              <Typography variant="body2" color="text.secondary" mb={1}>
                One or more addresses of the accounts team. Each address gets one e-mail per PO.
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={draft.recipients.length ? 1.25 : 0}>
                {draft.recipients.map((r) => (
                  <Chip key={r} label={r} onDelete={() => set({ recipients: draft.recipients.filter((x) => x !== r) })} />
                ))}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  label="Add an e-mail address"
                  value={emailDraft}
                  onChange={(e) => { setEmailDraft(e.target.value); setEmailError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                  error={!!emailError}
                  helperText={emailError || (!draft.recipients.length ? 'Add at least one address.' : ' ')}
                  inputProps={{ inputMode: 'email' }}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" onClick={addEmail} sx={{ mt: 0.25 }}>Add</Button>
              </Stack>
            </Step>

            <Step n={3} title="Payment terms" required>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Only a PO with one of these payment terms is e-mailed. A PO whose payment terms are not ticked in any active rule sends
                nothing. On the right: how many of this company&apos;s POs{windowText ? ` of ${windowText}` : ''} had those terms.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} mb={1}>
                <TextField size="small" label="Search payment terms" value={termQuery} onChange={(e) => setTermQuery(e.target.value)} sx={{ flex: 1 }} />
                <Button size="small" onClick={() => set({ paymentterms: Array.from(new Set([...draft.paymentterms, ...shownTerms.map((t) => t.code)])).sort((a, b) => a - b) })}>
                  Tick all shown
                </Button>
                <Button size="small" onClick={() => set({ paymentterms: draft.paymentterms.filter((c) => !shownTerms.some((t) => t.code === c)) })}>
                  Untick all shown
                </Button>
                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {lists ? `${draft.paymentterms.length} of ${terms.length} ticked` : `${draft.paymentterms.length} ticked`}
                </Typography>
              </Stack>
              {!lists ? <CircularProgress size={20} aria-label="Loading the payment terms" /> : null}
              <Box sx={{ maxHeight: 340, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {shownTerms.map((t) => {
                  const checked = draft.paymentterms.includes(t.code);
                  const count = termCounts?.[String(t.code)] || 0;
                  return (
                    <Box
                      key={t.code}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, borderBottom: 1, borderColor: 'divider', bgcolor: checked ? 'action.selected' : 'transparent' }}
                    >
                      <FormControlLabel
                        sx={{ flex: 1, mr: 0, minWidth: 0 }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={(e) => set({
                              paymentterms: e.target.checked
                                ? Array.from(new Set([...draft.paymentterms, t.code])).sort((a, b) => a - b)
                                : draft.paymentterms.filter((c) => c !== t.code),
                            })}
                          />
                        )}
                        label={<span>{t.name} <Typography component="span" variant="caption" color="text.secondary">({t.code})</Typography></span>}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{count ? `${count} PO${count === 1 ? '' : 's'}` : '—'}</Typography>
                    </Box>
                  );
                })}
                {lists && !shownTerms.length ? <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>No payment terms match the search.</Typography> : null}
              </Box>
            </Step>

            <Step n={4} title="Projects">
              <KindToggle value={draft.projects} onChange={(v) => set({ projects: v })} allLabel="All projects" someLabel="Only these projects" label="Projects" />
              {draft.projects.all ? (
                <Typography variant="body2" color="text.secondary">Every project of this company, including projects added later.</Typography>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">A PO matches when its header or any of its lines carries one of the chosen projects.</Typography>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    limitTags={10}
                    options={projectItems}
                    value={draft.projects.codes.map((c) => projectItems.find((p) => p.code === c) || { code: c, name: '', division: '' })}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    getOptionLabel={(o) => `${o.code}${o.name ? ` · ${o.name}` : ''}`}
                    onChange={(_e, next) => set({ projects: { all: false, codes: next.map((o) => o.code) } })}
                    renderInput={(params) => <TextField {...params} label={`Projects of this company (${projectItems.length})`} placeholder="Search by code or name" />}
                  />
                  <FormControl size="small" sx={{ maxWidth: 380 }}>
                    <InputLabel id="rule-project-division">Or add every project of a division</InputLabel>
                    <Select
                      labelId="rule-project-division"
                      label="Or add every project of a division"
                      value=""
                      onChange={(e) => {
                        const division = String(e.target.value || '');
                        if (!division) return;
                        const codes = projectItems.filter((p) => p.division === division).map((p) => p.code);
                        set({ projects: { all: false, codes: Array.from(new Set([...draft.projects.codes, ...codes])) } });
                      }}
                    >
                      {divisionOptions.map((d) => (
                        <MenuItem key={d.code} value={d.code} disabled={!d.count}>{d.code} · {d.name} ({d.count} projects)</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!draft.projects.codes.length ? <Typography variant="body2" color="warning.main">Pick at least one project, or choose All projects.</Typography> : null}
                </Stack>
              )}
            </Step>

            <Step n={5} title="Divisions (SAP Dimension 1)">
              <KindToggle value={draft.divisions} onChange={(v) => set({ divisions: v })} allLabel="All divisions" someLabel="Only these divisions" label="Divisions" />
              {draft.divisions.all ? (
                <Typography variant="body2" color="text.secondary">Every division of this company.</Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>Matched on the Division of the PO&apos;s lines.</Typography>
                  <CodeGrid items={lists?.divisions || []} value={draft.divisions} onChange={(v) => set({ divisions: v })} emptyText="This company has no synced divisions." />
                  {!draft.divisions.codes.length ? <Typography variant="body2" color="warning.main">Tick at least one division, or choose All divisions.</Typography> : null}
                </>
              )}
            </Step>

            <Step n={6} title="Departments (SAP Dimension 2)">
              <KindToggle value={draft.departments} onChange={(v) => set({ departments: v })} allLabel="All departments" someLabel="Only these departments" label="Departments" />
              {draft.departments.all ? (
                <Typography variant="body2" color="text.secondary">Every department of this company.</Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>Matched on the Department of the PO&apos;s lines (and the cost center of a PRPO PO).</Typography>
                  <CodeGrid items={lists?.departments || []} value={draft.departments} onChange={(v) => set({ departments: v })} emptyText="This company has no synced departments." />
                  {!draft.departments.codes.length ? <Typography variant="body2" color="warning.main">Tick at least one department, or choose All departments.</Typography> : null}
                </>
              )}
            </Step>
          </Stack>

          <Box sx={{ flex: '1 1 300px', minWidth: 0, position: { md: 'sticky' }, top: { md: 0 } }}>
            <Stack spacing={2} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.default' }}>
              <Box>
                <Typography variant="overline" color="text.secondary">What this rule does</Typography>
                <Typography variant="body2">{ruleSentence(draft, lists)}</Typography>
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h4" component="p" sx={{ fontWeight: 700, lineHeight: 1 }}>{match ? match.count : '–'}</Typography>
                  {matching ? <CircularProgress size={14} aria-label="Counting" /> : null}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {match
                    ? (match.poCount
                      ? `of this company's ${match.poCount} POs of ${match.from} – ${match.to} would have been e-mailed.`
                      : 'No synced SAP purchase orders of this company to count yet.')
                    : 'POs this rule would have e-mailed.'}
                </Typography>
              </Box>
              {match?.recent?.length ? (
                <Box>
                  <Typography variant="overline" color="text.secondary">Most recent matches</Typography>
                  {match.recent.map((m: any) => (
                    <Box key={m.docEntry} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>PO {m.docNum}</Typography>
                        <Typography variant="body2">{money(m.currency, m.total)}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">{m.docDate} · {m.vendor}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        terms {terms.find((t) => t.code === m.termCode)?.name || m.term}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null}
              <Button variant="outlined" onClick={openPreview} disabled={previewing}>
                {previewing ? 'Preparing the preview…' : 'Preview the e-mail'}
              </Button>
              {problems.length ? (
                <Alert severity="warning">
                  <Typography variant="subtitle2">Before saving</Typography>
                  {problems.map((p) => <Typography key={p} variant="body2">• {p}</Typography>)}
                </Alert>
              ) : (
                <Alert severity="success">Ready to save.</Alert>
              )}
              <Button size="small" onClick={sendTest} disabled={testing}>{testing ? 'Sending…' : 'Send a test e-mail to me'}</Button>
              <Typography variant="caption" color="text.secondary">
                The preview and the test use a real PO of this company approved recently (the newest this rule matches). Nothing is sent to the
                recipients until the rule is saved, active, and the company is switched on.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </DraggableDialog>

      <DraggableDialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={narrow}
        title="Preview of the accounts e-mail"
        actions={<Button onClick={() => setPreview(null)}>Close</Button>}
      >
        {preview ? (
          <Stack spacing={1.5}>
            <Typography variant="body2"><strong>Subject:</strong> {preview.subject}</Typography>
            <Box
              component="iframe"
              title="Accounts e-mail preview"
              sandbox=""
              srcDoc={preview.html}
              sx={{ width: '100%', height: { xs: '70vh', md: '72vh' }, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
            />
          </Stack>
        ) : null}
      </DraggableDialog>
    </>
  );
}
